import { useCallback, useMemo } from "react";
import { toast } from "sonner";

import {
  getCallConnection,
  invokeCallHub,
  registerHandler,
  startCallConnection,
} from "@/realtime/callHubClient.js";
import { CALL_STATES } from "@/calling/callConstants.js";
import { useCallEngine } from "@/calling/hooks/useCallEngine.js";
import { CALL_HUB_EVENTS, CALL_HUB_METHODS } from "@/calling/realtime/callEvents.js";
import {
  departmentDisplayName,
  HOTEL_DEPARTMENTS,
  normalizeDepartment,
} from "@/utils/alerts/departmentAlerts.js";

const callIdFrom = (payload = {}) => payload.callId || payload.CallId || "";

const createVisitorTarget = (department = HOTEL_DEPARTMENTS.reception) => {
  const targetDepartment = normalizeDepartment(department, HOTEL_DEPARTMENTS.reception);
  const targetDepartmentLabel = departmentDisplayName(targetDepartment);

  return {
    targetId: targetDepartment,
    targetType: "department",
    targetLabel: `Calling ${targetDepartmentLabel}`,
    subtitle: `Connecting to ${targetDepartmentLabel}...`,
    metadata: {
      targetDepartment,
      targetDepartmentLabel,
    },
  };
};

const createVisitorIncomingDisplay = (payload = {}) => {
  const targetDepartment = normalizeDepartment(payload.targetDepartment || payload.TargetDepartment || HOTEL_DEPARTMENTS.reception);
  const targetDepartmentLabel = departmentDisplayName(targetDepartment);
  const callerName =
    payload.staffName ||
    payload.StaffName ||
    payload.callerName ||
    payload.CallerName ||
    targetDepartmentLabel;

  return {
    ...payload,
    callId: callIdFrom(payload),
    title: `${callerName} is calling`,
    subtitle: `Hotel ${targetDepartmentLabel} desk`,
    targetDepartment,
  };
};

const createVisitorHubClient = ({ connection, token, visitorName, metadata }) => {
  const registerVisitor = () => invokeCallHub(connection, "RegisterWebVisitor", token, visitorName || "Website Visitor");

  return {
    connection,
    startConnection: () => startCallConnection({ mode: "web_visitor", webContactToken: token }),
    invoke: (methodName, payload = {}) => {
      if (
        methodName === CALL_HUB_METHODS.registerParticipant ||
        methodName === CALL_HUB_METHODS.registerReceiver
      ) {
        return registerVisitor();
      }

      if (methodName === CALL_HUB_METHODS.startCall) {
        const targetDepartment = payload.target?.metadata?.targetDepartment || payload.target?.targetId;
        return invokeCallHub(connection, "StartWebVisitorCall", {
          token,
          visitorName: visitorName || "Website Visitor",
          targetDepartment: normalizeDepartment(targetDepartment, metadata?.defaultDepartment || HOTEL_DEPARTMENTS.reception),
        });
      }

      return invokeCallHub(connection, methodName, payload);
    },
    registerHandler: (eventName, handler) => {
      const eventAliases = {
        [CALL_HUB_EVENTS.participantRegistered]: ["WebVisitorRegistered", "WebVisitorPresenceChanged"],
        [CALL_HUB_EVENTS.receiverRegistered]: ["WebVisitorRegistered", "WebVisitorPresenceChanged"],
        [CALL_HUB_EVENTS.incomingCall]: ["IncomingCall", "IncomingReceptionCall", "incomingcall"],
      }[eventName] || [eventName];

      const cleanups = eventAliases.map((alias) => registerHandler(connection, alias, handler));

      return () => {
        cleanups.forEach((cleanup) => {
          try {
            cleanup?.();
          } catch {
            // Ignore cleanup errors
          }
        });
      };
    },
  };
};

export const useWebVisitorCall = ({ token, visitorName, metadata, remoteAudioRef }) => {
  const organizationId = metadata?.organizationId || "";
  const title = metadata?.title || "Website Concierge";

  const connection = useMemo(
    () => (token ? getCallConnection({ mode: "web_visitor", webContactToken: token }) : null),
    [token],
  );

  const identity = useMemo(() => {
    if (!token) return null;
    return {
      participantId: token,
      participantType: "web-visitor",
      displayName: visitorName || "Website Visitor",
      tenantId: organizationId,
      metadata: {
        title,
        token,
      },
    };
  }, [token, visitorName, organizationId, title]);

  const receiveScopes = useMemo(
    () => (token ? [`web-visitor:${token}`] : []),
    [token],
  );

  const hubClient = useMemo(
    () =>
      connection && token
        ? createVisitorHubClient({
            connection,
            token,
            visitorName,
            metadata,
          })
        : null,
    [connection, token, visitorName, metadata],
  );

  const engine = useCallEngine({
    hubClient,
    identity,
    receiveScopes,
    remoteAudioRef,
    mode: "receiver",
    mapIncomingCall: createVisitorIncomingDisplay,
    onError: (message) => toast.error(message),
  });

  const status =
    engine.registrationState === "registered"
      ? "connected"
      : engine.registrationState || "idle";
  const registered = engine.registrationState === "registered";

  const callDepartment = useCallback(
    async (department = HOTEL_DEPARTMENTS.reception) => {
      if (!registered) {
        toast.error("Connecting to hotel call desk...");
        try {
          await hubClient?.startConnection();
        } catch {
          toast.error("Realtime call network unavailable. Please try again.");
          return;
        }
      }

      const target = createVisitorTarget(department);
      await engine.startCall(target);
    },
    [engine, hubClient, registered],
  );

  return {
    callState: engine.callState,
    callInfo: engine.callInfo,
    activeCall: engine.activeCall,
    incomingCall: engine.incomingCall,
    status,
    registered,
    lastError: engine.lastError,
    lastEndReason: engine.lastEndReason,
    muted: engine.muted,
    isHeld: engine.isHeld,
    isRemoteHeld: engine.isRemoteHeld,
    isTransferring: engine.isTransferring,
    transferringDepartment: engine.transferringDepartment,
    callDuration: engine.callDuration,
    callDepartment,
    acceptCall: engine.acceptCall,
    declineCall: engine.declineCall,
    endCall: engine.endCall,
    toggleMute: engine.toggleMute,
    retryRegistration: engine.retryRegistration,
    isCalling: engine.callState === CALL_STATES.calling,
    isConnected: engine.callState === CALL_STATES.connected,
    isIncoming: engine.callState === CALL_STATES.incoming,
    isConnecting: engine.callState === CALL_STATES.connecting,
  };
};
